import { useState, useEffect, useRef, useContext, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "services/api";
import AppContext from "contexts/AppContext";
import moneyFormatter from "services/moneyFormatter";
import ls from "services/ls";
import { Modal, Alert, Dropdown, Form, Popover, OverlayTrigger } from "react-bootstrap";
import DOMPurify from "dompurify";

import Translator, { __ } from "services/Translator";
import swal from "sweetalert2";
import BalanceTransfer from "components/BalanceTransfer";
import Vault from "components/Vault";
import { useSelector, useDispatch } from "react-redux";
import { showWalletModal, hideWalletModal } from "stores/ui/uiStates";
import config from "configs/config";
import { getBonusIconMeta } from "utils/getBonusIcon";
import ModalCloseButton from "components/ModalCloseButton";
import { getInitialSettings } from "utils/initialSettings";

// --- Rillpay PSP (GoldenClover-only) ---------------------------------------
// Rillpay sub-methods are returned by /deposit/methods with method_id values
// prefixed "Rillpay:". The card method opens a card form; all others post the
// amount + method and follow the standard redirect flow.
const RILLPAY_METHOD_PREFIX = "Rillpay:";
const RILLPAY_CARD_METHOD = "Rillpay:CC";

// Brand-compliance (Rillpay MID-termination rule): Skrill / Neteller /
// Paysafecard MUST be shown as exact text-only labels "SKR" / "NET" / "PSC",
// never a brand name or logo — regardless of what the backend returns.
const RILLPAY_DISPLAY_OVERRIDES = {
  "Rillpay:SKR": "SKR",
  "Rillpay:NET": "NET",
  "Rillpay:PSC": "PSC",
};

const EMPTY_RILLPAY_CARD = {
  cardholderName: "",
  cardNumber: "",
  expiryMonth: "",
  expiryYear: "",
  cvv: "",
};

// Luhn check for the card number (digits only). PCI: operates on a local value,
// never stored or logged.
function isValidLuhn(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length < 13 || digits.length > 19) return false;
  let sum = 0;
  let shouldDouble = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let digit = parseInt(digits.charAt(i), 10);
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0;
}

// Group the card number in blocks of 4 for display (digits only, max 19).
function formatCardNumber(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 19);
  return digits.replace(/(.{4})/g, "$1 ").trim();
}

function CashierModals(props) {
  const dispatch = useDispatch();
  const { getUserBalance } = useContext(AppContext);
  const userBalance = useSelector((state) => state.userBalance);
  const uiStates = useSelector((state) => state.uiStates);
  const setUsersWallet = props.setUsersWallet;
  const showInPlay = false;
  const wageringTooltipRef = useRef(null);
  const [wageringTooltipShow, setWageringTooltipShow] = useState(false);

  const [profile, setProfile] = useState({});
  const getProfile = async () => {
    var response = await api.get("/profile");
    if (response.status === 200) {
      setProfile(response.data);
    }
  };

  useEffect(() => {
    getProfile();
  }, []);

  useEffect(() => {
    var timeout;
    if (wageringTooltipShow) {
      timeout = setTimeout(() => {
        setWageringTooltipShow(false);
      }, 3000);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [wageringTooltipShow]);

  const [withdrawalMethods, setWithdrawalMethods] = useState([]);
  const [withdrawalMethod, setWithdrawalMethod] = useState(null);
  const [withdrawalAmount, setWithdrawalAmount] = useState("");

  const walletModalVisible = uiStates.walletModal;
  const openWalletModal = async (tab = "deposit") => {
    if (props.cashierRedesignEnabled) {
      props.setOpenWalletModalTab(tab === "withdraw" ? "withdraw" : "deposit");
      return;
    }
    if (userBalance?.wallets?.length) {
      dispatch(showWalletModal());

      setWithdrawalMessage(null);
      setWithdrawalMessageType("success");
      fetchCryptoCoins();
    }
  };

  const closeWalletModal = () => {
    dispatch(hideWalletModal());
    props.setOpenWalletModalTab(null);
  };

  const [withdrawalMessage, setWithdrawalMessage] = useState(null);
  const [withdrawalMessageType, setWithdrawalMessageType] = useState("success");
  const [withdrawalExtraFields, setWithdrawalExtraFields] = useState(
    config.environment === "production"
      ? {}
      : {
          account: "002010077777777771",
          account_type: "clabe",
          bank_code: "40002",
          document_id: "MAMB780915969",
          document_type: "RFC",
        }
  );

  const updateWithdrawalExtraFields = (field, value) => {
    setWithdrawalExtraFields((prev) => ({ ...prev, [field]: value }));
  };

  const getWithdrawalMethods = async () => {
    // if (withdrawalAmount) {
    props.setSpinner(true);
    var response = await api.post("/withdraw/methods", {
      amount: withdrawalAmount,
    });
    props.setSpinner(false);
    if (response.status === 200) {
      setWithdrawalMethods(response.data.methods);
      if (withdrawalMethod) {
        if (!response.data.methods.filter((method) => method.method_id === withdrawalMethod.method_id).length) {
          setWithdrawalMethod(null);
          setWithdrawalExtraFields({});
        }
      }
    }
    // }
  };

  useEffect(() => {
    getWithdrawalMethods();
  }, [withdrawalAmount]);

  const withdraw = async () => {
    if (profile.emailVerified) {
      props.setSpinner(true);
      var amount = parseFloat(withdrawalAmount);
      if (!amount || isNaN(amount)) {
        setWithdrawalMessage("Please enter a valid amount for withdrawal");
        setWithdrawalMessageType("danger");
        props.setSpinner(false);
        return false;
      } else if (parseFloat(amount) < minWitdrawalAmountInPlayerCurrency) {
        setWithdrawalMessage("Minimum amount for withdrawal is " + minWitdrawalAmountInPlayerCurrency);
        setWithdrawalMessageType("danger");
        props.setSpinner(false);
        return false;
      }

      var postData = {
        amount: amount,
        method: withdrawalMethod.method_id,
        extraFields: withdrawalExtraFields,
      };
      var response = await api.post("/withdraw", {
        ...postData,
        twofa_code: twoFaCode,
      });
      props.setSpinner(false);
      if (response.status === 200) {
        setWithdrawalAmount("");
        setWithdrawalExtraFields("");
        setWithdrawalMethod(null);
        setTwoFaCode("");
        setWithdrawalMessage("Your withdrawal request has been received");
        setWithdrawalMessageType("success");

        setTimeout(() => {
          setWithdrawalMessage(null);
        }, 3000);
      } else {
        props.showAlert(null, __(response.data.message), true);
      }
    } else {
      return new swal({
        title: __("Attention!"),
        text: __("Please verify your e-mail address before requesting a withdraw. We have send you an email for this."),

      }).then(function () {
        // quickLogin(newRegForm.email, newRegForm.password);
        // setShowWalletModal(false);
        // history.push("/dashboard/verify");
        resendActivationMail();
      });
    }
  };

  // wallet tabs
  const [activeWalletTab, setActiveWalletTab] = useState("deposit"); //deposit,withdrawal
  const [activeDepositTab, setActiveDepositTab] = useState("fiat"); //crypto,fiat
  const [activeFiatTab, setActiveFiatTab] = useState(null);

  const toggleWalletTab = (tabName) => {
    setActiveWalletTab(tabName);
  };

  // --- AlphaPo / CoinsPaid on-demand on-chain crypto deposit ---------------
  // On-demand model: the player picks a coin -> the backend creates that crypto
  // wallet (if needed) and returns an on-chain deposit address
  // (POST /deposit/crypto-address). The offered coin list comes from
  // GET /deposit/crypto-coins. SELF-GATING: the Crypto tab only renders when the
  // backend offers at least one coin (AlphaPo configured + live rates), so this
  // is inert on brands/envs without crypto — nothing existing is touched.
  const [cryptoCoins, setCryptoCoins] = useState([]);
  const [selectedDepositWallet, setSelectedDepositWallet] = useState(null);
  const [cryptoAddresses, setCryptoAddresses] = useState({}); // { COIN: { coin, address, tag, qr_text } }
  const [cryptoAddressLoading, setCryptoAddressLoading] = useState(false);
  const [cryptoAddressError, setCryptoAddressError] = useState(null);
  const [addressCopied, setAddressCopied] = useState(false);
  const [tagCopied, setTagCopied] = useState(false);

  const cryptoAvailable = cryptoCoins.length > 0;

  // Display-only hints (per-coin minimum deposit + required confirmations).
  const cryptoMinDeposits = {
    BTC: 0.0001,
    ETH: 0.01,
    BNB: 0.01,
    "BNB-BSC": 0.0001,
    LTC: 0.01,
    DOGE: 1,
    USDTE: 0.01,
    USDTT: 1,
    TRX: 10,
    XRP: 0.001,
  };
  const cryptoConfirmationsRequired = {
    BTC: 1,
    ETH: 10,
    BNB: 1,
    "BNB-BSC": 25,
    LTC: 6,
    DOGE: 6,
    USDTE: 10,
    USDTT: 19,
    TRX: 19,
    XRP: 3,
  };

  const fetchCryptoCoins = async () => {
    try {
      const response = await api.get("/deposit/crypto-coins");
      if (
        response.status === 200 &&
        response.data &&
        Array.isArray(response.data.coins)
      ) {
        setCryptoCoins(response.data.coins);
      }
    } catch (e) {
      // Crypto not available for this brand/env -> the Crypto tab stays hidden.
    }
  };

  // Lazily fetch (and cache) the on-chain deposit address for a coin.
  const fetchCryptoAddress = async (coin) => {
    if (!coin || cryptoAddresses[coin]) return;
    setCryptoAddressLoading(true);
    setCryptoAddressError(null);
    try {
      const response = await api.post("/deposit/crypto-address", { coin });
      if (response.status === 200 && response.data && response.data.address) {
        setCryptoAddresses((prev) => ({ ...prev, [coin]: response.data }));
      } else {
        setCryptoAddressError(
          (response.data && response.data.message) ||
            __("Could not generate a deposit address. Please try again.")
        );
      }
    } catch (e) {
      setCryptoAddressError(
        __("Could not generate a deposit address. Please try again.")
      );
    } finally {
      setCryptoAddressLoading(false);
    }
  };

  const selectCryptoCoin = (coin) => {
    setSelectedDepositWallet(coin);
    fetchCryptoAddress(coin);
  };

  const copyCryptoAddress = () => {
    const el = document.getElementById("crypto_deposit_address");
    if (!el) return;
    navigator.clipboard.writeText(el.value);
    setAddressCopied(true);
    setTimeout(() => setAddressCopied(false), 2000);
  };
  const copyCryptoTag = () => {
    const el = document.getElementById("crypto_deposit_tag");
    if (!el) return;
    navigator.clipboard.writeText(el.value);
    setTagCopied(true);
    setTimeout(() => setTagCopied(false), 2000);
  };

  useEffect(() => {
    if (props.cashierRedesignEnabled) return;
    if (props.openWalletModalTab) {
      if (props.openWalletModalTab === "deposit") {
        openWalletModal();
        setActiveWalletTab("deposit");
        setActiveDepositTab("fiat");
      } else if (props.openWalletModalTab === "withdraw") {
        openWalletModal();
        setActiveWalletTab("withdraw");
      }
    }
  }, [props.openWalletModalTab]);

  const [twoFaCode, setTwoFaCode] = useState("");

  const resendActivationMail = async (ev) => {
    props.setSpinner(true);
    var response = await api.post("/resend-activation-mail", {
      force: true,
      email: props.userDetails.email,
    });
    props.setSpinner(false);
    if (response.status == 200) {
      // alert("sent");
    } else {
    }
  };

  const resend2FaCode = async (ev) => {
    props.setSpinner(true);
    var response = await api.post("/resend-twofa");
    props.setSpinner(false);
    if (response.status == 200) {
      return new swal({
        className: "custom_swal",
        title: __("2FA code sent"),
        text: __(response.data.message),
        icon: "success",
      });
    }
  };

  useEffect(() => {
    setActiveFiatTab("lamecanica");
  }, [activeWalletTab, activeDepositTab]);

  const [bm2PayDepositAmount, setBm2PayDepositAmount] = useState(55.3); //dummy for testing

  const getFiatCurrencies = async () => {
    var response = await api.get("/currencies/true");
    var tmpCurrencies = [{ label: __("Select Currency"), value: "" }];
    if (response.status === 200) {
      response.data.map((item) => {
        tmpCurrencies.push({
          label: item.currency_code,
          value: item.currency_code,
        });
      });
    }
  };

  useEffect(() => {
    getFiatCurrencies();
  }, []);

  const testCardDetails =
    config.environment === "production"
      ? {
          cardholderName: "",
          cardNumber: "",
          expiryMonth: "",
          expiryYear: "",
          cvv: "",
        }
      : {
          cardholderName: "Matti Juhani Virtanen",
          cardNumber: "4111111111111111",
          expiryMonth: "12",
          expiryYear: "25",
          cvv: "123",
        };

  const [creditCardInfo, setCreditCardInfo] = useState(testCardDetails);

  const initBm2PayDeposit = async () => {
    if (!bm2PayDepositAmount || isNaN(parseFloat(bm2PayDepositAmount)) || !parseFloat(bm2PayDepositAmount)) {
      return new swal({

        text: __("Please enter an amount."),

      });
    }

    var depositData = {
      amount: bm2PayDepositAmount,
      method: "Bm2Pay:CC",
      campaign: "no_bonus",
    };

    depositData.cardDetails = creditCardInfo;

    props.setSpinner(true);
    var response = await api.post("/deposit", depositData);
    props.setSpinner(false);
    console.log("depositResponse", response);
    if (response.status === 200) {
      if (response.data.status === "additional_fields") {
      } else if (response.data.status === "redirect") {
        window.location.href = response.data.redirectUrl;
      }
    } else {
      props.showAlert(null, __(response.data.message));
    }
  };

  const initPagSmileDeposit = async () => {
    if (!depositAmount || isNaN(parseFloat(depositAmount)) || !parseFloat(depositAmount)) {
      return new swal({

        text: __("Please enter an amount."),

      });
    }

    var depositData = {
      amount: depositAmount,
      method: depositMethod || "PagSmile:Cashier",
      campaign: bonusCampaign,
    };

    props.setSpinner(true);
    var response = await api.post("/deposit", depositData);
    props.setSpinner(false);
    console.log("depositResponse", response);
    if (response.status === 200) {
      if (response.data.status === "redirect") {
        window.location.href = response.data.redirectUrl;
      }
    } else {
      props.showAlert(null, __(response.data.message));
    }
  };

  const initLaMecanicaDeposit = async () => {
    if (!depositAmount || isNaN(parseFloat(depositAmount)) || !parseFloat(depositAmount)) {
      return new swal({
        text: __("Please enter an amount."),
      });
    }

    var depositData = {
      amount: depositAmount,
      // Selected category-based method (e.g. "LAMECANICA:CARDS"); falls back to
      // the legacy default-routing method when no sub-methods are configured.
      method: lamecanicaMethod || "LAMECANICA:SALE",
      campaign: bonusCampaign,
    };

    props.setSpinner(true);
    var response = await api.post("/deposit", depositData);
    props.setSpinner(false);
    if (response.status === 200) {
      if (response.data.status === "redirect") {
        window.location.href = response.data.redirectUrl;
      }
    } else {
      props.showAlert(null, __(response.data.message));
    }
  };

  // Rillpay aggregator state (GoldenClover-only PSP). Card data lives only in
  // this transient local state and is cleared right after the deposit POST.
  const [rillpayMethod, setRillpayMethod] = useState(null);
  const [rillpayCard, setRillpayCard] = useState(EMPTY_RILLPAY_CARD);

  const initRillpayDeposit = async () => {
    if (!rillpayMethod) {
      return new swal({ text: __("Please select a payment method.") });
    }
    if (!depositAmount || isNaN(parseFloat(depositAmount)) || !parseFloat(depositAmount)) {
      return new swal({ text: __("Please enter an amount.") });
    }

    const isCard = rillpayMethod === RILLPAY_CARD_METHOD;

    if (isCard) {
      // Validate card input before sending. (Card data is never logged.)
      if (!isValidLuhn(rillpayCard.cardNumber)) {
        return new swal({ text: __("Please enter a valid card number.") });
      }
      const mm = parseInt(rillpayCard.expiryMonth, 10);
      if (!/^\d{2}$/.test(rillpayCard.expiryMonth) || mm < 1 || mm > 12) {
        return new swal({ text: __("Please enter a valid expiry month (MM).") });
      }
      if (!/^\d{4}$/.test(rillpayCard.expiryYear)) {
        return new swal({ text: __("Please enter a valid expiry year (YYYY).") });
      }
      if (!/^\d{3,4}$/.test(rillpayCard.cvv)) {
        return new swal({ text: __("Please enter a valid CVV.") });
      }
      if (!rillpayCard.cardholderName.trim()) {
        return new swal({ text: __("Please enter the cardholder name.") });
      }
    }

    const depositData = {
      amount: depositAmount,
      method: rillpayMethod,
      campaign: bonusCampaign,
    };

    if (isCard) {
      // Map to the Rillpay cardDetails contract. Billing fields are omitted; the
      // backend uses the player's profile data when not supplied.
      depositData.cardDetails = {
        cardNumber: String(rillpayCard.cardNumber).replace(/\D/g, ""),
        cardExpMM: rillpayCard.expiryMonth,
        cardExpYY: rillpayCard.expiryYear,
        cardCVV: rillpayCard.cvv,
        cardHolder: rillpayCard.cardholderName.trim(),
      };
    } else {
      depositData.paymentDetails = {};
    }

    props.setSpinner(true);
    const response = await api.post("/deposit", depositData);
    props.setSpinner(false);

    // PCI: drop card data from state immediately after the POST (no logging,
    // no storage, no analytics).
    setRillpayCard(EMPTY_RILLPAY_CARD);

    if (response.status === 200) {
      if (response.data.status === "redirect") {
        window.location.href = response.data.redirectUrl;
      } else if (response.data.message) {
        props.showAlert(null, __(response.data.message));
      }
    } else {
      props.showAlert(null, __(response.data.message));
    }
  };

  const [depositAmount, setDepositAmount] = useState(100);

  //Bonus Campaigns
  const [bonusCampaigns, setBonusCampaigns] = useState([]);
  const [bonusCampaign, setBonusCampaign] = useState("no_bonus");
  const [depositMethods, setDepositMethods] = useState([]);
  // BO-driven cashier sections. Only present when the cashier builder is active
  // (GCC theme + CASHIER_BUILDER_ENABLED); an empty array means fall back to the
  // legacy hardcoded tab layout, so flag-off and non-GCC brands are unaffected.
  const [depositSections, setDepositSections] = useState([]);
  const [depositMethod, setDepositMethod] = useState(null);
  // Selected La Mecanica sub-method (one PaymentMethod row per paymentPageCategory).
  const [lamecanicaMethod, setLamecanicaMethod] = useState(null);

  const [minDepositAmount, setMinDepositAmount] = useState(0);
  const [minWitdrawalAmount, setMinWitdrawalAmount] = useState(0);
  const [minWitdrawalAmountInPlayerCurrency, setMinWitdrawalAmountInPlayerCurrency] = useState(0);

  // Per-method maximum deposit (null = no max). Set alongside min from /deposit/methods.
  const [maxDepositAmount, setMaxDepositAmount] = useState(null);
  // Deposit presets + "Most Popular" come from CMS (initial-settings.cashier_settings).
  // Defaults apply until/if the CMS values load, so the cashier never renders empty.
  const [presetDenominations, setPresetDenominations] = useState([25, 50, 100, 250, 500]);
  const [popularPreset, setPopularPreset] = useState(100);
  // Promo banner (image + link) from CMS cashier_settings. Null until loaded; the
  // banner only renders when an image URL is present.
  const [cashierBannerImage, setCashierBannerImage] = useState(null);
  const [cashierBannerLink, setCashierBannerLink] = useState(null);

  useEffect(() => {
    if (withdrawalMethod && userBalance) {
      setMinWitdrawalAmount(withdrawalMethod.min_withdrawal);
      setMinWitdrawalAmountInPlayerCurrency(
        moneyFormatter.convertCurrencyToFiat(withdrawalMethod.min_withdrawal, "USD", userBalance.currency_code)
      );
    }
  }, [userBalance, withdrawalMethod]);

  const getDepositMethods = async () => {
    var response = await api.post("/deposit/methods", {
      amount: depositAmount || 1000000,
    });
    if (response.status === 200) {
      const methods = response.data.methods || [];
      setDepositMethods(methods);
      // Additive: BO section metadata. Absent on flag-off / non-GCC -> [] -> the
      // component keeps the legacy layout.
      setDepositSections(Array.isArray(response.data.sections) ? response.data.sections : []);
      let currentSelection = depositMethod;
      if (!methods.find((m) => m.method_id === currentSelection)) {
        currentSelection = methods[0]?.method_id || null;
        setDepositMethod(currentSelection);
      }
      const selected = methods.find((m) => m.method_id === currentSelection);
      if (selected) {
        setMinDepositAmount(selected.min_deposit);
        setMaxDepositAmount(selected.max_deposit ?? null);
      }
    }
  };

  useEffect(() => {
    getDepositMethods();
  }, [depositAmount]);

  useEffect(() => {
    const selected = depositMethods.find((m) => m.method_id === depositMethod);
    if (selected) {
      setMinDepositAmount(selected.min_deposit);
      setMaxDepositAmount(selected.max_deposit ?? null);
    }
  }, [depositMethod, depositMethods]);

  // Load CMS-driven cashier settings (preset denominations + "Most Popular").
  useEffect(() => {
    (async () => {
      try {
        const s = await getInitialSettings();
        const cs = s?.cashier_settings;
        if (!cs) return;
        if (cs.denominations) {
          const parsed = String(cs.denominations)
            .split(",")
            .map((v) => parseInt(v.trim(), 10))
            .filter((v) => !isNaN(v) && v > 0);
          if (parsed.length) setPresetDenominations(parsed);
        }
        const pop = parseInt(cs.popularPreset, 10);
        setPopularPreset(!isNaN(pop) && pop > 0 ? pop : null);
        setCashierBannerImage(cs.bannerImage || null);
        setCashierBannerLink(cs.bannerLink || null);
      } catch (e) {
        // Keep defaults on failure; never block the cashier.
      }
    })();
  }, []);

  // Single source of the deposit min/max helper line. Uses the selected method's
  // min_deposit/max_deposit (already in player currency conversion via moneyFormatter).
  const renderDepositMinMaxText = () => {
    const minTxt = moneyFormatter.convertCurrencyToFiat(minDepositAmount, "USD", userBalance.currency_code);
    const hasMax = maxDepositAmount !== null && maxDepositAmount !== undefined && maxDepositAmount !== "";
    const maxTxt = hasMax
      ? moneyFormatter.convertCurrencyToFiat(maxDepositAmount, "USD", userBalance.currency_code)
      : null;
    return __("Min. deposit amount is") + " " + minTxt + (maxTxt ? " · " + __("Max") + " " + maxTxt : "");
  };

  //Fetches the available campaigns based on the deposit amount
  //Used if deposit button directly linked to praxis
  const getDepositCampaigns = async () => {
    var response = await api.post("/deposit/campaigns", {
      amount: depositAmount,
    });
    if (response.status === 200) {
      setBonusCampaigns(response.data.campaigns);
    }
  };

  const updateNextDepositBonus = async (campaignId) => {
    var prevCampaignId = bonusCampaign;
    setBonusCampaign(campaignId);
    var response = await api.post("/deposit/save-next-bonus", {
      campaign_id: campaignId,
    });
    if (response.status !== 200) {
      setBonusCampaign(prevCampaignId);
    }
  };

  useEffect(() => {
    getDepositCampaigns();
  }, [depositAmount]);

  // Rillpay sub-methods come from /deposit/methods (same source as PagSmile).
  const rillpayMethods = depositMethods.filter(
    (m) => typeof m.method_id === "string" && m.method_id.startsWith(RILLPAY_METHOD_PREFIX)
  );

  // La Mecanica sub-methods: one PaymentMethod row per paymentPageCategory
  // (method id "LAMECANICA:<CATEGORY>"), surfaced and gated by the back office
  // (enable/disable, min deposit, country/currency excludes) like any method.
  const lamecanicaMethods = depositMethods.filter(
    (m) => typeof m.method_id === "string" && m.method_id.startsWith("LAMECANICA:")
  );
  // Keep a valid selection: default to the first available La Mecanica method.
  useEffect(() => {
    if (!lamecanicaMethods.length) return;
    if (!lamecanicaMethods.find((m) => m.method_id === lamecanicaMethod)) {
      setLamecanicaMethod(lamecanicaMethods[0].method_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depositMethods]);
  // Display name with compliance overrides for SKR/NET/PSC.
  const getRillpayDisplayName = (method) =>
    RILLPAY_DISPLAY_OVERRIDES[method.method_id] || method.method || method.method_id;
  // Show a logo only for non-override methods AND only when the backend supplies
  // one (no brand-logo guessing for the MVP; SKR/NET/PSC are always text-only).
  const showRillpayLogo = (method) =>
    !RILLPAY_DISPLAY_OVERRIDES[method.method_id] && Boolean(method.logo);

  // --- BO-driven cashier sections (PR-4) -------------------------------------
  // Active only when the serving layer returns a non-empty `sections` list
  // (GCC theme + CASHIER_BUILDER_ENABLED). Otherwise the component renders the
  // untouched legacy layout below, so flag-off and non-GCC brands are identical.
  const cashierBuilderActive = depositSections.length > 0;

  // Canonical badge order + display copy. The serving layer already filters and
  // orders to this whitelist; we re-apply defensively so nothing unexpected shows.
  const BADGE_LABELS = { instant: "Instant", recommended: "Recommended", popular: "Popular" };
  const BADGE_ORDER = ["instant", "recommended", "popular"];
  const getMethodBadges = (method) => {
    const raw = Array.isArray(method?.badges) ? method.badges : [];
    return BADGE_ORDER.filter((b) => raw.includes(b));
  };

  // Localized label from the serving contract, with the SKR/NET/PSC compliance
  // overrides applied on top (same rule as the legacy Rillpay grid).
  const getMethodDisplayLabel = (method) =>
    RILLPAY_DISPLAY_OVERRIDES[method.method_id] ||
    method.display_label ||
    method.method ||
    method.method_id;

  // Map a method_id prefix to its PSP submit family. Used ONLY to dispatch the
  // deposit to the correct, unchanged init function; routing itself is untouched.
  const getMethodFamily = (methodId) => {
    if (typeof methodId !== "string") return "generic";
    if (methodId.startsWith("LAMECANICA:")) return "lamecanica";
    if (methodId.startsWith(RILLPAY_METHOD_PREFIX)) return "rillpay";
    // Nylo (NYLO:<APM>, e.g. NYLO:WLT / NYLO:PIX2) is intentionally generic: one
    // tile per APM comes from /deposit/methods (backend-gated on PSP_DRIVER_NYLO,
    // rows created disabled), the generic init posts the method_id to /deposit and
    // the backend answers { status: "redirect", redirectUrl } to Nylo's hosted
    // page; the player returns via /payment-status/:status like every redirect PSP.
    if (methodId.startsWith("NYLO:")) return "generic";
    // Monellyn (MONELLYN:<SUFFIX>, PayAdmit hosted checkout) is intentionally
    // generic for the same reason: tiles come from /deposit/methods (backend-gated
    // on PSP_DRIVER_MONELLYN, rows created disabled), the generic init posts the
    // method_id to /deposit and follows { status: "redirect", redirectUrl } to the
    // PayAdmit-hosted page; the player returns via /payment-status/:status.
    if (methodId.startsWith("MONELLYN:")) return "generic";
    // MicaPay (MICAPAY:<SUFFIX>, e.g. MICAPAY:CARD, hosted checkout) is intentionally
    // generic for the same reason: tiles come from /deposit/methods (backend-gated on
    // PSP_DRIVER_MICAPAY, rows created disabled), the generic init posts the method_id
    // to /deposit and follows { status: "redirect", redirectUrl } to the MicaPay-hosted
    // page; the player returns via /payment-status/:status.
    if (methodId.startsWith("MICAPAY:")) return "generic";
    // Bm2Pay is intentionally NOT a section family: its legacy init uses separate
    // dummy-amount + card state that the sections body does not render, so a
    // Bm2Pay method in a section falls through to the generic redirect deposit
    // (correct amount + method_id, no dummy state). Bm2Pay is not in GCC's live
    // PSP set; this guard is purely defensive.
    return "generic"; // PagSmile and any other single-step redirect method
  };

  // Group enabled methods under their section slug; methods without a matching
  // enabled section fall into a synthetic "Other" group (contract par 5A).
  const sectionGroups = depositSections
    .map((section) => ({
      slug: section.slug,
      heading: section.heading,
      subheading: section.subheading,
      methods: depositMethods.filter((m) => m.section === section.slug),
    }))
    .filter((group) => group.methods.length > 0);
  const orphanMethods = depositMethods.filter(
    (m) => m.type !== "Crypto" && !depositSections.some((s) => s.slug === m.section)
  );

  // Tab list: real sections (with methods) + Other (if orphans) + Crypto (which
  // is a separate mechanism, coins come from /deposit/crypto-coins, not sections).
  const _fiatTabs = sectionGroups.map((g) => ({ key: g.slug, label: g.heading || g.slug }));
  const _otherTab = orphanMethods.length ? [{ key: "__other", label: __("Other") }] : [];
  const _cryptoTab = cryptoAvailable ? [{ key: "__crypto", label: __("Crypto") }] : [];
  // Crypto tab sits right after the first section (requested order: Bank & Local, Crypto, E-wallets, Cards)
  const sectionTabs = _fiatTabs.length
    ? [_fiatTabs[0], ..._cryptoTab, ..._fiatTabs.slice(1), ..._otherTab]
    : [..._cryptoTab, ..._otherTab];

  const [activeSection, setActiveSection] = useState(null);

  const chooseSection = (key) => setActiveSection(key);

  // Keep a valid active section as data arrives / changes.
  useEffect(() => {
    if (!cashierBuilderActive) return;
    const keys = sectionTabs.map((t) => t.key);
    if (!keys.length) return;
    if (!activeSection || !keys.includes(activeSection)) {
      setActiveSection(keys[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depositSections, depositMethods, cryptoAvailable]);

  // Methods shown under the active (non-crypto) section.
  const activeSectionMethods =
    activeSection === "__other"
      ? orphanMethods
      : sectionGroups.find((g) => g.slug === activeSection)?.methods || [];

  // Select a method inside a section: set the generic selection (drives min/max)
  // plus the family-specific state that the UNCHANGED init functions read.
  const selectSectionMethod = (method) => {
    const id = method.method_id;
    setDepositMethod(id);
    const family = getMethodFamily(id);
    if (family === "lamecanica") setLamecanicaMethod(id);
    else if (family === "rillpay") setRillpayMethod(id);
  };

  // Resolve + select the section's method when the active section changes or the
  // method list reloads. We ALWAYS call selectSectionMethod (for the in-section
  // current method, else the first) so the family-specific state the init reads
  // (rillpayMethod / lamecanicaMethod) is seeded even for the default selection.
  // Deps exclude depositMethod, so an in-section user click does not re-trigger it.
  useEffect(() => {
    if (!cashierBuilderActive) return;
    if (activeSection === "__crypto") return;
    if (!activeSectionMethods.length) return;
    const inSection = activeSectionMethods.find((m) => m.method_id === depositMethod);
    selectSectionMethod(inSection || activeSectionMethods[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, depositMethods]);

  // Dispatch the deposit to the correct existing init (submit flows untouched).
  const submitSectionDeposit = () => {
    const family = getMethodFamily(depositMethod);
    if (family === "lamecanica") return initLaMecanicaDeposit();
    if (family === "rillpay") return initRillpayDeposit();
    // Generic redirect deposit: posts the selected method_id + the section amount.
    return initPagSmileDeposit();
  };

  // Section tab bar (replaces the hardcoded PSP-prefix switch when active).
  const renderSectionTabs = () => (
    <div className="cashier-section-tabs">
      {sectionTabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className={"cashier-section-tab" + (activeSection === tab.key ? " active" : "")}
          onClick={() => chooseSection(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  // Body for a non-crypto section: amount zone + BO-driven method grid + (card
  // form for Rillpay:CC) + bonus campaigns + a deposit button that dispatches to
  // the correct, unchanged PSP init. Crypto is handled by the legacy crypto view.
  const renderSectionsBody = () => {
    const activeGroup = sectionGroups.find((g) => g.slug === activeSection);
    return (
      <div className="custom_deposit cashier-sections-body">
        <h3>{__("Deposit")}</h3>
        {activeGroup && activeGroup.subheading ? (
          <p className="cashier-section-sub">{activeGroup.subheading}</p>
        ) : null}

        <strong>{__("Amount")}</strong>
        <div className="preset-amounts-grid">
          {presetDenominations.map((amt) => {
            const currencyCode = userBalance.wallets?.[0]?.currency || userBalance.currency_code || "USD";
            const formattedAmt = new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: currencyCode,
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(amt);
            const isSelected = parseFloat(depositAmount) === amt;
            return (
              <div
                key={amt}
                className={`preset-amount-btn-wrapper ${amt === popularPreset ? "popular" : ""}`}
              >
                {amt === popularPreset && <span className="popular-badge">{__("Most Popular")}</span>}
                <button
                  type="button"
                  className={`preset-amount-btn ${isSelected ? "active" : ""}`}
                  onClick={() => setDepositAmount(amt.toString())}
                >
                  {formattedAmt}
                </button>
              </div>
            );
          })}
        </div>
        <div className="form_area" style={{ marginTop: "0px" }}>
          <div className="input_item">
            <input
              type="number"
              className="custom_ut_value form-control name_value"
              placeholder={__("Amount")}
              value={depositAmount.toString()}
              onChange={(ev) => setDepositAmount(ev.target.value)}
            />
            <br />
            <div>{renderDepositMinMaxText()}</div>
          </div>
        </div>

        <div className="cashier-method-grid">
          {activeSectionMethods.length ? (
            activeSectionMethods.map((method) => {
              const selected = method.method_id === depositMethod;
              const badges = getMethodBadges(method);
              return (
                <button
                  key={method.method_id}
                  type="button"
                  className={"rillpay-method-card cashier-method-card" + (selected ? " is-selected" : "")}
                  onClick={() => selectSectionMethod(method)}
                >
                  {badges.length > 0 && (
                    <span className="cashier-method-card__badges">
                      {badges.map((b) => (
                        <span key={b} className={"cashier-badge cashier-badge--" + b}>
                          {__(BADGE_LABELS[b])}
                        </span>
                      ))}
                    </span>
                  )}
                  {showRillpayLogo(method) ? (
                    <img className="rillpay-method-card__logo" src={method.logo} alt="" />
                  ) : null}
                  <span className="rillpay-method-card__name">{getMethodDisplayLabel(method)}</span>
                  {method.promo_text ? (
                    <span className="cashier-method-card__promo">{method.promo_text}</span>
                  ) : null}
                  <span className="rillpay-method-card__mm">
                    {__("Min")} {method.min_deposit_in_player_currency}
                    {method.max_deposit_in_player_currency
                      ? " · " + __("Max") + " " + method.max_deposit_in_player_currency
                      : ""}
                  </span>
                </button>
              );
            })
          ) : (
            <div className="rillpay-empty">{__("No payment methods available right now.")}</div>
          )}
        </div>

        {depositMethod === RILLPAY_CARD_METHOD && renderRillpayCardForm()}

        {renderBonusCampaigns()}

        <div className="login_button">
          <Link
            hreflang={Translator.getSelectedLanguage().code}
            onClick={submitSectionDeposit}
            className="commonBtn pTwoBtn"
          >
            {__("Deposit")}
          </Link>
        </div>
      </div>
    );
  };

  // Bonus campaigns selector (GCC "gcc-bonus-option" cards). Shared by the legacy
  // Quick Deposit tab and the new sections layout; same handlers and state.
  const renderBonusCampaigns = () => (
    <div className="campaigns-wrap">
      <label>
        <h3 style={{ fontSize: "1.2rem" }}>{__("Bonus Campaigns")}</h3>
      </label>
      <div className="campaigns">
        {bonusCampaigns.length ? (
          <>
            {bonusCampaigns.map((campaign, index) => {
              const iconMeta = getBonusIconMeta(campaign);
              const active = bonusCampaign === campaign.bonus_campaign.id;
              const plainDesc = campaign.bonus_campaign.description
                ? campaign.bonus_campaign.description.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
                : "";

              return (
                <button
                  key={index}
                  type="button"
                  className={`gcc-bonus-option ${active ? "is-selected" : ""}`}
                  onClick={() => updateNextDepositBonus(campaign.bonus_campaign.id)}
                >
                  <span className="gcc-bonus-option__radio" aria-hidden="true" />
                  <span className="gcc-bonus-option__text">
                    <span className="gcc-bonus-option__title">
                      {campaign.bonus_campaign.title}
                    </span>
                    <span className="gcc-bonus-option__sub">
                      {__("Minimum deposit for this bonus") + ": "}
                      {userBalance.wallets?.[0]?.currency || userBalance.currency_code || ""}
                      {campaign.minimum_deposit}
                      {plainDesc ? ` | ${plainDesc}` : ""}
                    </span>
                  </span>
                  {iconMeta?.src ? (
                    <img
                      src={iconMeta.src}
                      alt=""
                      className={`gcc-bonus-option__thumb ${
                        iconMeta.isFallback ? "gcc-bonus-option__thumb--icon" : ""
                      }`}
                    />
                  ) : null}
                </button>
              );
            })}
            <button
              type="button"
              className={`gcc-bonus-option ${
                bonusCampaign === "no_bonus" ? "is-selected" : ""
              }`}
              onClick={() => updateNextDepositBonus("no_bonus")}
            >
              <span className="gcc-bonus-option__radio" aria-hidden="true" />
              <span className="gcc-bonus-option__text">
                <span className="gcc-bonus-option__title">{__("No Bonus")}</span>
                <span className="gcc-bonus-option__sub">
                  {__("Deposit without claiming any bonus campaign")}
                </span>
              </span>
              <img
                src="https://cdn.ninestatic.com/front/components/register/gift-icon.png?id=1"
                alt=""
                className="gcc-bonus-option__thumb gcc-bonus-option__thumb--icon"
              />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="gcc-bonus-option is-selected"
              disabled
            >
              <span className="gcc-bonus-option__radio" aria-hidden="true" />
              <span className="gcc-bonus-option__text">
                {profile.acceptsBonus ? (
                  <>
                    <span className="gcc-bonus-option__title">
                      {__("No bonus available")}
                    </span>
                    <span className="gcc-bonus-option__sub">
                      {__("There are currently no active deposit bonus campaigns")}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="gcc-bonus-option__title">
                      {__("You have chosen not to accept bonus")}
                    </span>
                    <span className="gcc-bonus-option__sub">
                      {__("Enable bonus acceptance in your profile settings to receive offers")}
                    </span>
                  </>
                )}
              </span>
            </button>
          </>
        )}
      </div>
    </div>
  );

  // Rillpay card form (Rillpay:CC). Shared by the legacy Rillpay tab and the new
  // sections layout. Card data lives only in transient state and is cleared right
  // after the deposit POST inside initRillpayDeposit (PCI: never logged/stored).
  const renderRillpayCardForm = () => (
    <div className="credit-card-form rillpay-card-form">
      <div className="input_item form-group">
        <label className="input_label">{__("Cardholder Name")}</label>
        <input
          type="text"
          className="name_value form-control"
          placeholder={__("Enter cardholder name")}
          autoComplete="cc-name"
          value={rillpayCard.cardholderName}
          onChange={(e) =>
            setRillpayCard({
              ...rillpayCard,
              cardholderName: e.target.value,
            })
          }
        />
      </div>
      <div className="input_item form-group">
        <label className="input_label">{__("Card Number")}</label>
        <input
          type="text"
          inputMode="numeric"
          className="name_value form-control"
          placeholder={__("Enter card number")}
          autoComplete="cc-number"
          maxLength="23"
          value={rillpayCard.cardNumber}
          onChange={(e) =>
            setRillpayCard({
              ...rillpayCard,
              cardNumber: formatCardNumber(e.target.value),
            })
          }
        />
      </div>
      <div className="input_item form-group">
        <label className="input_label">{__("Expiry Date")}</label>
        <div className="expiry-date-inputs d-flex">
          <input
            type="text"
            inputMode="numeric"
            className="name_value form-control"
            placeholder={__("MM")}
            maxLength="2"
            autoComplete="cc-exp-month"
            value={rillpayCard.expiryMonth}
            onChange={(e) =>
              setRillpayCard({
                ...rillpayCard,
                expiryMonth: e.target.value.replace(/\D/g, "").slice(0, 2),
              })
            }
          />
          <span className="separator"> / </span>
          <input
            type="text"
            inputMode="numeric"
            className="name_value form-control"
            placeholder={__("YYYY")}
            maxLength="4"
            autoComplete="cc-exp-year"
            value={rillpayCard.expiryYear}
            onChange={(e) =>
              setRillpayCard({
                ...rillpayCard,
                expiryYear: e.target.value.replace(/\D/g, "").slice(0, 4),
              })
            }
          />
        </div>
      </div>
      <div className="input_item form-group">
        <label className="input_label">{__("CVV")}</label>
        <input
          type="password"
          inputMode="numeric"
          className="name_value form-control"
          placeholder={__("Enter CVV")}
          maxLength="4"
          autoComplete="cc-csc"
          value={rillpayCard.cvv}
          onChange={(e) =>
            setRillpayCard({
              ...rillpayCard,
              cvv: e.target.value.replace(/\D/g, "").slice(0, 4),
            })
          }
        />
      </div>
    </div>
  );

  return (
    <>
      {!props.modalsOnly && (
      <div className="deposit_block ">
        <Dropdown>
          <Dropdown.Toggle
            className="right wallets_dropdown"
            id="dropdown-basic1"
            // onClick={() => {
            //   props.setLeftSidebarSlide(false);
            // }}
          >
            <div className="wallet_caret">
              <i className="fas downarrow fa-caret-down"></i>
              <i className="fas uparrow fa-caret-up"></i>
            </div>

            {!showInPlay && (
              <>
                <div className="deposit_icon">
                  {!!userBalance.wallets?.length && moneyFormatter.currencyIcon(userBalance.wallets[0].currency)}
                </div>
                <div
                  onClick={() => {
                    props.setLeftSidebarSlide(false);
                  }}
                  className="deposit_amount"
                >
                  {!!userBalance.wallets?.length
                    ? parseFloat(userBalance.wallets[0].balance + userBalance.wallets[0].bonus_balance_crypto).toFixed(
                        2
                      )
                    : ""}
                </div>
              </>
            )}
            {showInPlay && (
              <div onClick={() => getUserBalance()} className="deposit_amount">
                {__("In Play")}
              </div>
            )}
          </Dropdown.Toggle>

          <Dropdown.Menu className="wallets_menu desktop">
            <div className="modal_top_arrow"></div>
            <div>
              <div className="wallets_head row">
                <div className="col-6"></div>
                <div className="col-6 text-right">{__("Balance")}</div>
              </div>

              <div className="wallets_menu_body">
                {!!userBalance.wallets?.length &&
                  userBalance.wallets.map((item, index) => {
                    return (
                      <Dropdown.Item as="button" key={index}>
                        <div className="wallets_balance row" key={index} onClick={() => setUsersWallet(item.currency)}>
                          <div className="col-6">{parseFloat(item.balance).toFixed(2)}</div>
                          <div className="col-6 currency">{moneyFormatter.currencyWithIcon(item.currency)}</div>
                        </div>
                      </Dropdown.Item>
                    );
                  })}
                {userBalance.bonus_balance && (
                  <Dropdown.Item as="button">
                    <div className="wallets_balance row">
                      <div className="col-6">
                        {parseFloat(userBalance.bonus_balance).toFixed(2)}
                      </div>

                      <div
                        ref={wageringTooltipRef}
                        onMouseOver={() => setWageringTooltipShow(true)}
                        onMouseOut={() => setWageringTooltipShow(false)}
                        className="col-6 text-right currency"
                      >
                        <span className="currency_icon bonusIcon">
                          <img
                            style={{ width: "20px" }}
                            src={require("assets/play365/images/cashierModal/star-icon.svg").default}
                            alt=""
                          />
                        </span>
                        <span className="currency_name">{__("BONUS")}</span>

                        <br />
                      </div>
                      <div className="progress_bar">
                        <div className="progress">
                          <div
                            style={{
                              width: userBalance.wagering_percentage + "%",
                            }}
                            className="progress-bar"
                          ></div>
                        </div>
                      </div>
                    </div>
                  </Dropdown.Item>
                )}
              </div>
            </div>
          </Dropdown.Menu>
        </Dropdown>

        <div className="deposit_button d-none d-md-block">
          <a onClick={openWalletModal} className="commonBtn pTwoBtn" href="#">
            {__("CASHIER")}
          </a>
        </div>
      </div>
      )}

      {/* dialogClassName: own hook so tablet widths can widen ONLY the cashier
          dialog (Bootstrap caps .modal-lg at 500px below 992px); withdraw/login
          and every other modal keep their default dialog. */}
      <Modal size="lg" centered show={props.cashierRedesignEnabled ? false : walletModalVisible} onHide={closeWalletModal} dialogClassName="cashier-modal-dialog">
        <Modal.Body className="cashier_modal">
          <ModalCloseButton onClick={closeWalletModal} />

          <div className="row">
            <div className="col-md-4 ">
              <div className="cashier_menu">
                <div className="modal_header_v1">
                  <div className="login_mode in_deposit_modal">
                    <Link
                      hreflang={Translator.getSelectedLanguage().code}
                      to="#"
                      onClick={() => toggleWalletTab("deposit")}
                      className={activeWalletTab === "deposit" ? "mode_btn active" : "mode_btn"}
                    >
                      {__("Deposit")}
                    </Link>
                    <Link
                      hreflang={Translator.getSelectedLanguage().code}
                      to="#"
                      onClick={() => toggleWalletTab("withdraw")}
                      className={activeWalletTab === "withdraw" ? "mode_btn active" : "mode_btn"}
                    >
                      {__("Withdraw")}
                    </Link>
                    {/* <Link
                      hreflang={Translator.getSelectedLanguage().code}
                      to="#"
                      onClick={() => toggleWalletTab("buy_crypto")}
                      className={
                        "d-none " +
                        (activeWalletTab === "buy_crypto"
                          ? "mode_btn active"
                          : "mode_btn")
                      }
                    >
                      {__("Buy Crypto")}
                    </Link>
                    <Link
                      hreflang={Translator.getSelectedLanguage().code}
                      onClick={() => toggleWalletTab("vault")}
                      className={
                        "" +
                        (activeWalletTab === "vault"
                          ? "mode_btn active"
                          : "mode_btn")
                      }
                    >
                      {__("Vault")}
                    </Link>
                    <Link
                      hreflang={Translator.getSelectedLanguage().code}
                      onClick={() => toggleWalletTab("tip")}
                      className={
                        "" +
                        (activeWalletTab === "tip"
                          ? "mode_btn active"
                          : "mode_btn")
                      }
                    >
                      {__("Tip")}
                    </Link> */}
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-8 ">
              <div className="cashier_content">
                {activeWalletTab === "deposit" && (
                  <>
                    {/* CMS-driven promo banner (cashier_settings.bannerImage/bannerLink).
                        Gated on cashierBuilderActive so the legacy (flag-off) cashier
                        stays byte-identical; only renders when an image URL is set. */}
                    {cashierBuilderActive &&
                      cashierBannerImage &&
                      (cashierBannerLink ? (
                        <a
                          className="cashier-banner"
                          href={cashierBannerLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <img className="cashier-banner__img" src={cashierBannerImage} alt="" />
                        </a>
                      ) : (
                        <div className="cashier-banner">
                          <img className="cashier-banner__img" src={cashierBannerImage} alt="" />
                        </div>
                      ))}
                    <div className="form_area">
                      <div className="modal_header_v1">
                        {/* cashier-builder-wrap: lets mobile CSS stack the section
                            tabs + sections body (the legacy .login_mode flex row was
                            built for the old mode_btn landing and squeezed them side
                            by side). Flag-off (legacy) markup stays byte-identical. */}
                        <div
                          className={
                            "login_mode in_deposit_modal" +
                            (cashierBuilderActive ? " cashier-builder-wrap" : "")
                          }
                        >
                          {activeDepositTab === "fiat" && (
                            <>
                              {/* BO-driven cashier (PR-4): when the builder is active, render
                                  section tabs + the sections body; otherwise the untouched
                                  legacy layout below (all legacy blocks gated on
                                  !cashierBuilderActive, crypto shared via activeSection). */}
                              {cashierBuilderActive && renderSectionTabs()}
                              {cashierBuilderActive &&
                                activeSection !== "__crypto" &&
                                renderSectionsBody()}
                              {!cashierBuilderActive && activeFiatTab === null ? (
                                <div className="modal_header_v2">
                                  <div className="login_mode in_deposit_modal narrow">
                                    <Link
                                      hrefLang={Translator.getSelectedLanguage().code}
                                      to="#"
                                      onClick={() => {
                                        setActiveFiatTab("bm2pay");
                                      }}
                                      className={activeFiatTab === "bm2pay" ? "mode_btn active" : "mode_btn"}
                                    >
                                      <div className="big_method_button">
                                        {/* <div className="method_logo">
                                          <img
                                            style={{
                                              height: "40px",
                                              marginTop: "27px",
                                            }}
                                            src={require("assets/jackpotlatino/images/cashierModal/utorg.png")}
                                          />
                                        </div> */}
                                        <div className="method_main_text">
                                          {__("Credit Card")}
                                          <div className="subtext">{__("Pay with credit card")}</div>
                                        </div>
                                      </div>
                                    </Link>

                                    <Link
                                      hrefLang={Translator.getSelectedLanguage().code}
                                      to="#"
                                      onClick={() => {
                                        setActiveFiatTab("pagsmile");
                                      }}
                                      className={activeFiatTab === "pagsmile" ? "mode_btn active" : "mode_btn"}
                                    >
                                      <div className="big_method_button">
                                        <div className="method_main_text">
                                          {__("E-Wallet")}
                                          <div className="subtext">{__("Pay with e-wallet")}</div>
                                        </div>
                                      </div>
                                    </Link>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  {/* <Link
                                    hreflang={
                                      Translator.getSelectedLanguage().code
                                    }
                                    to="#"
                                    onClick={() => setActiveFiatTab(null)}
                                    className="mode_btn"
                                  >
                                    &lArr;
                                  </Link> */}
                                  {!cashierBuilderActive &&
                                    (activeFiatTab === "lamecanica" ||
                                      activeFiatTab === "rillpay" ||
                                      activeFiatTab === "crypto") && (
                                    <div className="rillpay-method-switch">
                                      <button
                                        type="button"
                                        className={
                                          "rillpay-method-switch__btn" +
                                          (activeFiatTab === "lamecanica" ? " active" : "")
                                        }
                                        onClick={() => setActiveFiatTab("lamecanica")}
                                      >
                                        {__("Quick Deposit")}
                                      </button>
                                      <button
                                        type="button"
                                        className={
                                          "rillpay-method-switch__btn" +
                                          (activeFiatTab === "rillpay" ? " active" : "")
                                        }
                                        onClick={() => setActiveFiatTab("rillpay")}
                                      >
                                        {__("Other Payment Methods")}
                                      </button>
                                      {cryptoAvailable && (
                                        <button
                                          type="button"
                                          className={
                                            "rillpay-method-switch__btn" +
                                            (activeFiatTab === "crypto" ? " active" : "")
                                          }
                                          onClick={() => setActiveFiatTab("crypto")}
                                        >
                                          {__("Crypto")}
                                        </button>
                                      )}
                                    </div>
                                  )}
                                  {!cashierBuilderActive && activeFiatTab === "bm2pay" && (
                                    <>
                                      <div className="form_area">
                                        <div className="input_item">
                                          <div className="input_label">
                                            {__("Amount")}
                                            <span className="requried_icon">*</span>
                                          </div>
                                          <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={bm2PayDepositAmount}
                                            onChange={(ev) => setBm2PayDepositAmount(ev.target.value)}
                                            className=" name_value"
                                          />
                                        </div>

                                        <br />

                                        <div className="credit-card-form">
                                          <div className="input_item form-group">
                                            <label className="input_label">{__("Cardholder Name")}</label>
                                            <input
                                              type="text"
                                              className="name_value form-control"
                                              placeholder={__("Enter cardholder name")}
                                              value={creditCardInfo.cardholderName}
                                              onChange={(e) =>
                                                setCreditCardInfo({
                                                  ...creditCardInfo,
                                                  cardholderName: e.target.value,
                                                })
                                              }
                                            />
                                          </div>
                                          <div className="input_item form-group">
                                            <label className="input_label">{__("Card Number")}</label>
                                            <input
                                              type="text"
                                              className="name_value form-control"
                                              placeholder={__("Enter card number")}
                                              value={creditCardInfo.cardNumber}
                                              onChange={(e) =>
                                                setCreditCardInfo({
                                                  ...creditCardInfo,
                                                  cardNumber: e.target.value,
                                                })
                                              }
                                            />
                                          </div>
                                          <div className="input_item form-group">
                                            <label className="input_label">{__("Expiry Date")}</label>
                                            <div className="expiry-date-inputs d-flex">
                                              <input
                                                type="text"
                                                className="name_value form-control"
                                                placeholder={__("MM")}
                                                maxLength="2"
                                                value={creditCardInfo.expiryMonth}
                                                onChange={(e) =>
                                                  setCreditCardInfo({
                                                    ...creditCardInfo,
                                                    expiryMonth: e.target.value,
                                                  })
                                                }
                                              />
                                              <span className="separator"> / </span>
                                              <input
                                                type="text"
                                                className="name_value form-control"
                                                placeholder={__("YYYY")}
                                                maxLength="4"
                                                value={creditCardInfo.expiryYear}
                                                onChange={(e) =>
                                                  setCreditCardInfo({
                                                    ...creditCardInfo,
                                                    expiryYear: e.target.value,
                                                  })
                                                }
                                              />
                                            </div>
                                          </div>
                                          <div className="input_item form-group">
                                            <label className="input_label">{__("CVV")}</label>
                                            <input
                                              type="password"
                                              className="name_value form-control"
                                              placeholder={__("Enter CVV")}
                                              maxLength="4"
                                              value={creditCardInfo.cvv}
                                              onChange={(e) =>
                                                setCreditCardInfo({
                                                  ...creditCardInfo,
                                                  cvv: e.target.value,
                                                })
                                              }
                                              autocomplete="cc-csc"
                                            />
                                          </div>
                                        </div>

                                        <div className="login_button">
                                          <Link
                                            hreflang={Translator.getSelectedLanguage().code}
                                            onClick={initBm2PayDeposit}
                                            className="commonBtn pTwoBtn"
                                          >
                                            {__("Deposit")}
                                          </Link>
                                        </div>
                                      </div>
                                    </>
                                  )}

                                  {!cashierBuilderActive && activeFiatTab === "pagsmile" && (
                                    <div className="custom_deposit">
                                      <h3>{__("Deposit")}</h3>
                                      {/*customDepositAmountOpen && ( */}
                                      <>
                                        <strong>{__("Amount")}</strong>
                                        <div className="form_area" style={{ marginTop: "0px" }}>
                                          <div className="input_item">
                                            <input
                                              type="number"
                                              className="custom_ut_value form-control name_value "
                                              placeholder={__("Amount")}
                                              value={depositAmount.toString()}
                                              onChange={(ev) => setDepositAmount(parseInt(ev.target.value).toString())}
                                            />
                                            <br />
                                            <div>
                                              {renderDepositMinMaxText()}
                                            </div>
                                          </div>
                                        </div>
                                      </>
                                      {/*)*/}
                                      <div style={{ margin: "16px 0" }}>
                                        <h3 style={{ fontSize: "1.2rem", marginBottom: "8px", color: "#fff" }}>
                                          {__("Payment Method")}
                                        </h3>
                                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                          {depositMethods.map((method) => (
                                            <label
                                              key={method.method_id}
                                              htmlFor={"pm_" + method.method_id}
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                padding: "10px 12px",
                                                background:
                                                  method.method_id === depositMethod
                                                    ? "rgba(255,255,255,0.08)"
                                                    : "transparent",
                                                border:
                                                  method.method_id === depositMethod
                                                    ? "1px solid #FFC107"
                                                    : "1px solid rgba(255,255,255,0.15)",
                                                borderRadius: "6px",
                                                cursor: "pointer",
                                                transition: "all 0.15s ease",
                                              }}
                                            >
                                              <input
                                                id={"pm_" + method.method_id}
                                                type="radio"
                                                checked={method.method_id === depositMethod}
                                                onChange={() => setDepositMethod(method.method_id)}
                                                style={{ marginRight: "12px", cursor: "pointer" }}
                                              />
                                              {method.logo && (
                                                <div
                                                  style={{
                                                    backgroundImage: "url(" + method.logo + ")",
                                                    backgroundSize: "contain",
                                                    backgroundRepeat: "no-repeat",
                                                    backgroundPosition: "center",
                                                    width: "80px",
                                                    height: "32px",
                                                    marginRight: "12px",
                                                    flexShrink: 0,
                                                  }}
                                                />
                                              )}
                                              <span style={{ color: "#fff", fontSize: "14px" }}>{method.method}</span>
                                            </label>
                                          ))}
                                        </div>
                                      </div>
                                      <div className="campaigns-wrap">
                                        <label>
                                          <h3 style={{ fontSize: "1.2rem" }}>{__("Bonus Campaigns")}</h3>
                                        </label>
                                        <div className="campaigns">
                                          {bonusCampaigns.length ? (
                                            <>
                                              {bonusCampaigns.map((campaign, index) => (
                                                <div className="campaign">
                                                  <input
                                                    type="radio"
                                                    id={campaign.uniqid + "_id"}
                                                    value={campaign.bonus_campaign.id}
                                                    checked={bonusCampaign === campaign.bonus_campaign.id}
                                                    onChange={(ev) =>
                                                      updateNextDepositBonus(campaign.bonus_campaign.id)
                                                    }
                                                  />
                                                  <label htmlFor={campaign.uniqid + "_id"} className="check_box">
                                                    <i className="fas fa-check"></i>
                                                  </label>{" "}
                                                  <label htmlFor={campaign.uniqid + "_id"} className="campaign_label">
                                                    {campaign.bonus_campaign.title} (
                                                    {__("Minimum deposit for this bonus") + ": "} {userBalance.wallets?.[0]?.currency || userBalance.currency_code || ""}
                                                    {campaign.minimum_deposit})
                                                    {campaign.bonus_campaign.campaign_information && (
                                                      <OverlayTrigger
                                                        trigger="hover"
                                                        placement="top"
                                                        overlay={
                                                          <Popover title={__("Campaign Info")}>
                                                            <div
                                                              style={{
                                                                padding: 15,
                                                              }}
                                                              dangerouslySetInnerHTML={{
                                                                __html: DOMPurify.sanitize(
                                                                  campaign.bonus_campaign.description
                                                                ),
                                                              }}
                                                            ></div>
                                                          </Popover>
                                                        }
                                                      >
                                                        <a className="info_popover" href="javascript:">
                                                          <i className="fa fa-info-circle" aria-hidden="true"></i>{" "}
                                                        </a>
                                                      </OverlayTrigger>
                                                    )}
                                                  </label>
                                                  <div className="clearfix"></div>
                                                </div>
                                              ))}
                                              <div className="campaign">
                                                <input
                                                  id="no_bonus"
                                                  checked={bonusCampaign === "no_bonus"}
                                                  onChange={() => updateNextDepositBonus("no_bonus")}
                                                  type="radio"
                                                  value="no_bonus"
                                                />
                                                <label htmlFor="no_bonus" className="check_box">
                                                  <i className="fas fa-check"></i>
                                                </label>{" "}
                                                <label htmlFor="no_bonus" className="campaign_label">
                                                  {__("No Bonus")}
                                                </label>
                                              </div>
                                            </>
                                          ) : (
                                            <div className="campaign">
                                              <input
                                                id="no_bonus"
                                                checked={true}
                                                type="radio"
                                                onChange={() => updateNextDepositBonus("no_bonus")}
                                              />
                                              <label htmlFor="no_bonus" className="check_box">
                                                <i className="fas fa-check"></i>
                                              </label>
                                              {profile.acceptsBonus ? (
                                                <>
                                                  <label htmlFor="no_bonus" className="campaign_label">
                                                    {__("No bonus available")}
                                                  </label>
                                                </>
                                              ) : (
                                                <label htmlFor="no_bonus" className="campaign_label">
                                                  {__("You have chosen not to accept bonus")}
                                                </label>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <div className="login_button">
                                        <Link
                                          hreflang={Translator.getSelectedLanguage().code}
                                          onClick={initPagSmileDeposit}
                                          className="commonBtn pTwoBtn"
                                        >
                                          {__("Deposit")}
                                        </Link>
                                      </div>
                                    </div>
                                  )}

                                  {!cashierBuilderActive && activeFiatTab === "lamecanica" && (
                                    <div className="custom_deposit">
                                      <h3>{__("Deposit")}</h3>
                                      <>
                                        <strong>{__("Amount")}</strong>
                                        <div className="preset-amounts-grid">
                                          {presetDenominations.map((amt) => {
                                            const currencyCode = userBalance.wallets?.[0]?.currency || userBalance.currency_code || "USD";
                                            const formattedAmt = new Intl.NumberFormat("en-US", {
                                              style: "currency",
                                              currency: currencyCode,
                                              minimumFractionDigits: 0,
                                              maximumFractionDigits: 0,
                                            }).format(amt);
                                            const isSelected = parseFloat(depositAmount) === amt;
                                            return (
                                              <div
                                                key={amt}
                                                className={`preset-amount-btn-wrapper ${amt === popularPreset ? "popular" : ""}`}
                                              >
                                                {amt === popularPreset && (
                                                  <span className="popular-badge">{__("Most Popular")}</span>
                                                )}
                                                <button
                                                  type="button"
                                                  className={`preset-amount-btn ${isSelected ? "active" : ""}`}
                                                  onClick={() => setDepositAmount(amt.toString())}
                                                >
                                                  {formattedAmt}
                                                </button>
                                              </div>
                                            );
                                          })}
                                        </div>
                                        <div className="form_area" style={{ marginTop: "0px" }}>
                                          <div className="input_item">
                                            <input
                                              type="number"
                                              className="custom_ut_value form-control name_value "
                                              placeholder={__("Amount")}
                                              value={depositAmount.toString()}
                                              onChange={(ev) => setDepositAmount(ev.target.value)}
                                            />
                                            <br />
                                            <div>
                                              {renderDepositMinMaxText()}
                                            </div>
                                          </div>
                                        </div>
                                      </>
                                      {renderBonusCampaigns()}
                                      {lamecanicaMethods.length > 1 && (
                                        <div style={{ margin: "16px 0" }}>
                                          <h3 style={{ fontSize: "1.2rem", marginBottom: "8px" }}>
                                            {__("Payment Method")}
                                          </h3>
                                          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                            {lamecanicaMethods.map((method) => (
                                              <label
                                                key={method.method_id}
                                                htmlFor={"lm_" + method.method_id}
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  padding: "10px 12px",
                                                  background:
                                                    method.method_id === lamecanicaMethod
                                                      ? "rgba(255,255,255,0.08)"
                                                      : "transparent",
                                                  border:
                                                    method.method_id === lamecanicaMethod
                                                      ? "1px solid #FFC107"
                                                      : "1px solid rgba(255,255,255,0.15)",
                                                  borderRadius: "6px",
                                                  cursor: "pointer",
                                                }}
                                              >
                                                <input
                                                  id={"lm_" + method.method_id}
                                                  type="radio"
                                                  name="lamecanica_method"
                                                  checked={method.method_id === lamecanicaMethod}
                                                  onChange={() => setLamecanicaMethod(method.method_id)}
                                                  style={{ marginRight: "12px", cursor: "pointer" }}
                                                />
                                                {method.logo && (
                                                  <div
                                                    style={{
                                                      backgroundImage: "url(" + method.logo + ")",
                                                      backgroundSize: "contain",
                                                      backgroundRepeat: "no-repeat",
                                                      backgroundPosition: "center",
                                                      width: "80px",
                                                      height: "32px",
                                                      marginRight: "12px",
                                                      flexShrink: 0,
                                                    }}
                                                  />
                                                )}
                                                <span style={{ fontSize: "14px" }}>{method.method}</span>
                                              </label>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      <div className="login_button">
                                        <Link
                                          hreflang={Translator.getSelectedLanguage().code}
                                          onClick={initLaMecanicaDeposit}
                                          className="commonBtn pTwoBtn"
                                        >
                                          {__("Deposit")}
                                        </Link>
                                      </div>
                                    </div>
                                  )}

                                  {!cashierBuilderActive && activeFiatTab === "rillpay" && (
                                    <div className="custom_deposit rillpay-deposit">
                                      <h3>{__("Deposit")}</h3>

                                      <strong>{__("Amount")}</strong>
                                      <div className="form_area" style={{ marginTop: "0px" }}>
                                        <div className="input_item">
                                          <input
                                            type="number"
                                            className="custom_ut_value form-control name_value"
                                            placeholder={__("Amount")}
                                            value={depositAmount.toString()}
                                            onChange={(ev) => setDepositAmount(ev.target.value)}
                                          />
                                          <br />
                                          <div>
                                            {renderDepositMinMaxText()}
                                          </div>
                                        </div>
                                      </div>

                                      <div className="rillpay-section">
                                        <h3 className="rillpay-section__title">{__("Payment Method")}</h3>
                                        {rillpayMethods.length ? (
                                          <div className="rillpay-method-grid">
                                            {rillpayMethods.map((method) => {
                                              const selected = method.method_id === rillpayMethod;
                                              return (
                                                <button
                                                  key={method.method_id}
                                                  type="button"
                                                  className={
                                                    "rillpay-method-card" + (selected ? " is-selected" : "")
                                                  }
                                                  onClick={() => setRillpayMethod(method.method_id)}
                                                >
                                                  {showRillpayLogo(method) ? (
                                                    <img
                                                      className="rillpay-method-card__logo"
                                                      src={method.logo}
                                                      alt=""
                                                    />
                                                  ) : null}
                                                  <span className="rillpay-method-card__name">
                                                    {getRillpayDisplayName(method)}
                                                  </span>
                                                  <span className="rillpay-method-card__mm">
                                                    {__("Min")} {method.min_deposit_in_player_currency}
                                                    {method.max_deposit_in_player_currency
                                                      ? " · " + __("Max") + " " + method.max_deposit_in_player_currency
                                                      : ""}
                                                  </span>
                                                </button>
                                              );
                                            })}
                                          </div>
                                        ) : (
                                          <div className="rillpay-empty">
                                            {__("No payment methods available right now.")}
                                          </div>
                                        )}
                                      </div>

                                      {rillpayMethod === RILLPAY_CARD_METHOD && renderRillpayCardForm()}

                                      <div className="login_button">
                                        <Link
                                          hreflang={Translator.getSelectedLanguage().code}
                                          onClick={initRillpayDeposit}
                                          className="commonBtn pTwoBtn"
                                        >
                                          {__("Deposit")}
                                        </Link>
                                      </div>
                                    </div>
                                  )}

                                  {(activeFiatTab === "crypto" ||
                                    (cashierBuilderActive && activeSection === "__crypto")) && (
                                    <div className="custom_deposit crypto-deposit">
                                      <h3>{__("Deposit")}</h3>

                                      <div className="input_item">
                                        <div className="input_label">{__("Cryptocurrency")}</div>
                                        <br />
                                        <div className="cashier-method-grid cashier-crypto-grid">
                                          {cryptoCoins.map((coin, index) => (
                                            <div
                                              key={index}
                                              role="button"
                                              tabIndex={0}
                                              className={"rillpay-method-card cashier-method-card cashier-crypto-card" + (selectedDepositWallet === coin ? " is-selected" : "")}
                                              onClick={() => selectCryptoCoin(coin)}
                                            >
                                              <span className="cashier-crypto-card__icon">{moneyFormatter.currencyIcon(coin)}</span>
                                              <span className="rillpay-method-card__name">{moneyFormatter.currencyName(coin)}</span>
                                            </div>
                                          ))}
                                        </div>
                                        <div className="clearfix"></div>
                                      </div>

                                      {selectedDepositWallet && (
                                        <>
                                          <div className="privacy_text bordered_text">
                                            {cryptoMinDeposits[selectedDepositWallet] != null && (
                                              <>
                                                {__("The minimum deposit amount for")}{" "}
                                                {moneyFormatter.currencyName(selectedDepositWallet)} {__("is")}{" "}
                                                {moneyFormatter.currencyIcon(selectedDepositWallet)}{" "}
                                                {cryptoMinDeposits[selectedDepositWallet]}.{" "}
                                                {__(
                                                  "Depositing a lower amount may result in a full loss of the transferred amount."
                                                )}
                                                <br />
                                              </>
                                            )}
                                            {__("Only send")} {selectedDepositWallet}{" "}
                                            {__("to this address")}.{" "}
                                            {cryptoConfirmationsRequired[selectedDepositWallet] != null && (
                                              <>
                                                {cryptoConfirmationsRequired[selectedDepositWallet]}{" "}
                                                {__("confirmation")}
                                                {cryptoConfirmationsRequired[selectedDepositWallet] > 1 ? "s" : ""}{" "}
                                                {__("required")}.{" "}
                                              </>
                                            )}
                                            {__("We do not accept BEP20 from Binance.")}
                                          </div>

                                          {cryptoAddressLoading && (
                                            <div className="privacy_text">
                                              {__("Generating your deposit address...")}
                                            </div>
                                          )}
                                          {cryptoAddressError && (
                                            <Alert variant="danger">{cryptoAddressError}</Alert>
                                          )}

                                          {cryptoAddresses[selectedDepositWallet]?.address && (
                                          <div className="input_item walletAddress">
                                            <div className="input_label">
                                              {selectedDepositWallet} {__("Address")}
                                              <span className="requried_icon">*</span>
                                            </div>
                                            <input
                                              type="text"
                                              readOnly
                                              id="crypto_deposit_address"
                                              value={cryptoAddresses[selectedDepositWallet]?.address || ""}
                                              className="form-control name_value"
                                            />
                                            <span
                                              className="in_input_icon"
                                              role="button"
                                              tabIndex={0}
                                              style={{ cursor: "pointer" }}
                                              onClick={copyCryptoAddress}
                                            >
                                              <svg
                                                width="23"
                                                height="23"
                                                viewBox="0 0 23 23"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                              >
                                                <path
                                                  d="M16.0915 0H5.65821V5.93965H0V22.6502H16.491V16.7105H22.1493V6.35904L16.0915 0ZM16.3511 1.58759L20.6369 6.08658H16.3511V1.58759ZM15.6051 21.7201H0.885957V6.86968H9.8069V12.9563H15.6051V21.7201ZM10.6929 12.0262V7.52724L14.9787 12.0262H10.6929ZM21.2633 15.7804H16.491V12.2987L10.4333 5.93965H6.54422V0.930025H15.4651V7.0166H21.2633V15.7804Z"
                                                  fill="white"
                                                />
                                              </svg>
                                            </span>
                                            {addressCopied && (
                                              <span
                                                className="crypto-copied"
                                                style={{
                                                  marginLeft: "8px",
                                                  fontSize: "12px",
                                                  color: "#4caf50",
                                                }}
                                              >
                                                {__("Copied")}
                                              </span>
                                            )}
                                          </div>
                                          )}

                                          {cryptoAddresses[selectedDepositWallet]?.tag && (
                                            <div className="input_item walletAddress">
                                              <div className="input_label">
                                                {selectedDepositWallet} {__("Tag/Memo")}
                                                <span className="requried_icon">*</span>
                                              </div>
                                              <input
                                                type="text"
                                                readOnly
                                                id="crypto_deposit_tag"
                                                value={cryptoAddresses[selectedDepositWallet].tag}
                                                className="form-control name_value"
                                              />
                                              <span
                                                className="in_input_icon"
                                                role="button"
                                                tabIndex={0}
                                                style={{ cursor: "pointer" }}
                                                onClick={copyCryptoTag}
                                              >
                                                <svg
                                                  width="23"
                                                  height="23"
                                                  viewBox="0 0 23 23"
                                                  fill="none"
                                                  xmlns="http://www.w3.org/2000/svg"
                                                >
                                                  <path
                                                    d="M16.0915 0H5.65821V5.93965H0V22.6502H16.491V16.7105H22.1493V6.35904L16.0915 0ZM16.3511 1.58759L20.6369 6.08658H16.3511V1.58759ZM15.6051 21.7201H0.885957V6.86968H9.8069V12.9563H15.6051V21.7201ZM10.6929 12.0262V7.52724L14.9787 12.0262H10.6929ZM21.2633 15.7804H16.491V12.2987L10.4333 5.93965H6.54422V0.930025H15.4651V7.0166H21.2633V15.7804Z"
                                                    fill="white"
                                                  />
                                                </svg>
                                              </span>
                                              {tagCopied && (
                                                <span
                                                className="crypto-copied"
                                                style={{
                                                  marginLeft: "8px",
                                                  fontSize: "12px",
                                                  color: "#4caf50",
                                                }}
                                              >
                                                {__("Copied")}
                                              </span>
                                              )}
                                            </div>
                                          )}

                                          {cryptoAddresses[selectedDepositWallet]?.qr_text && (
                                            <div className="input_item">
                                              <div className="qr_code in_wallet_modal">
                                                <img
                                                  className="wallet_qr"
                                                  alt={__("Deposit QR code")}
                                                  src={
                                                    config.apiUrl +
                                                    "/qr-code/" +
                                                    cryptoAddresses[selectedDepositWallet].qr_text
                                                  }
                                                />
                                              </div>
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {activeWalletTab === "withdraw" && (
                  <>
                    <div className="form_area">
                      <div className="input_item">
                        <div className="input_label">{__("Withdraw")}</div>
                        <br />

                        {withdrawalMessage && <Alert variant={withdrawalMessageType}>{withdrawalMessage}</Alert>}

                        <div className="clearfix"></div>
                      </div>

                      <div className="input_item">
                        <div className="input_label">
                          {__("Amount")}
                          <span className="requried_icon">*</span>
                        </div>
                        <div className="input-group in_addon_area">
                          <input
                            type="number"
                            className={
                              " name_value " +
                              (withdrawalAmount < minWitdrawalAmountInPlayerCurrency || !withdrawalMethod
                                ? "error"
                                : "")
                            }
                            aria-label={__("Recipient's username")}
                            aria-describedby="basic-addon2"
                            value={withdrawalAmount}
                            onChange={(ev) => setWithdrawalAmount(ev.target.value)}
                          />
                          <div className="currency_name">{userBalance.wallets?.[0]?.currency}</div>
                          <span
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              if (userBalance.wallets?.[0]?.balance_fiat) {
                                setWithdrawalAmount(userBalance.wallets[0].balance_fiat.replace(",", ""));
                              }
                            }}
                            className="input-group-text max_lavel"
                            id="basic-addon2"
                          >
                            {__("MAX")}
                          </span>
                          {withdrawalAmount && withdrawalAmount < minWitdrawalAmountInPlayerCurrency && (
                            <div
                              className="error_message"
                              style={{
                                color: "red",
                                fontSize: "12px",
                                marginTop: "5px",
                              }}
                            >
                              {__("Minimum amount is " + minWitdrawalAmountInPlayerCurrency + "Please try again.")}
                            </div>
                          )}
                        </div>
                        <br />
                        <div className="input_item">
                          <div className="input_label">
                            {__("Select Method")}
                            <span className="requried_icon">*</span>
                          </div>
                          <div className="input-group in_addon_area"></div>
                          {withdrawalMethods.map((method, index) => (
                            <button
                              className={
                                "single_banking commonBtn  " +
                                (withdrawalMethod && method.method_id === withdrawalMethod.method_id
                                  ? "pOneBtn"
                                  : "pTwoBtn")
                              }
                              onClick={() => setWithdrawalMethod(method)}
                              style={{
                                marginRight: "10px",
                                marginBottom: "10px",
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                              }}
                            >
                              <div
                                className="method_logo"
                                style={{
                                  backgroundImage: "url(" + method.logo + ")",
                                }}
                              ></div>
                              <div className="method_name">
                                {method.method}{" "}
                                <sub>
                                  <small>
                                    {__("MIN")}{" "}
                                    {moneyFormatter.convertCurrencyToFiat(
                                      method.min_withdrawal,
                                      "USD",
                                      userBalance.currency_code
                                    )}
                                  </small>
                                </sub>
                              </div>
                            </button>
                          ))}
                        </div>

                        {/* <pre>{JSON.stringify(withdrawalExtraFields, null, 2)}</pre> */}

                        {withdrawalMethod && withdrawalMethod.method_id.startsWith("PAGSMILE::") && (
                          <>
                            {withdrawalMethod.fields.map((field, index) => (
                              <div className="input_item" key={index}>
                                <div className="input_label">
                                  {field.label}
                                  <span className="requried_icon">*</span>
                                </div>
                                {field.type === "text" && (
                                  <input
                                    type="text"
                                    value={withdrawalExtraFields[field.name]}
                                    onChange={(e) => updateWithdrawalExtraFields(field.name, e.target.value)}
                                    className="name_value"
                                  />
                                )}
                                {field.type === "select" && (
                                  <select
                                    value={withdrawalExtraFields[field.name]}
                                    onChange={(e) => updateWithdrawalExtraFields(field.name, e.target.value)}
                                    className="name_value"
                                  >
                                    <option value={""}>{__("Select")}</option>
                                    {Object.entries(field.values).map(([value, label], idx) => (
                                      <option key={idx} value={value}>
                                        {label}
                                      </option>
                                    ))}
                                  </select>
                                )}

                                {/* <i className="fal fa-copy in_input_icon"></i> */}
                              </div>
                            ))}
                          </>
                        )}

                        <div className="input-group-append">
                          <button
                            disabled={
                              !withdrawalAmount ||
                              withdrawalAmount < minWitdrawalAmountInPlayerCurrency ||
                              !withdrawalMethod
                            }
                            onClick={withdraw}
                            style={{
                              padding: "8px 16px",
                              borderRadius: "4px",
                              border: "none",
                              fontSize: "14px",
                              fontWeight: "bold",
                              cursor:
                                !withdrawalAmount ||
                                withdrawalAmount < minWitdrawalAmountInPlayerCurrency ||
                                !withdrawalMethod
                                  ? "not-allowed"
                                  : "pointer",
                              backgroundColor:
                                !withdrawalAmount ||
                                withdrawalAmount < minWitdrawalAmountInPlayerCurrency ||
                                !withdrawalMethod
                                  ? "#cccccc"
                                  : "#28a745",
                              color: "white",
                              minWidth: "80px",
                            }}
                          >
                            {__("Confirm")}
                          </button>
                        </div>
                      </div>

                      {profile.twoFaEnabled && (
                        <div className="input_item">
                          <div className="input_label">
                            {__("Two FA Code")}
                            <span className="requried_icon">*</span>
                            <br />
                            <span>{__("Please enter the twofa code sent to your registered email.")}</span>
                          </div>
                          <div className="input-group">
                            <input
                              type="number"
                              className=" name_value"
                              value={twoFaCode}
                              onChange={(ev) => setTwoFaCode(ev.target.value)}
                            />
                          </div>

                          <a style={{ color: "white" }} href="#" onClick={resend2FaCode}>
                            {__("Get 2FA Code")}
                          </a>
                        </div>
                      )}
                      <br />
                      <div className="login_button">
                        <Link className="commonBtn">
                          {withdrawalAmount >= minWitdrawalAmountInPlayerCurrency
                            ? __("Withdrawing") +
                              " " +
                              (userBalance.wallets?.[0]?.currency ||
                                userBalance.currency_code ||
                                "") +
                              " " +
                              withdrawalAmount
                            : minWitdrawalAmountInPlayerCurrency
                            ? __("Min amount is") + " " + minWitdrawalAmountInPlayerCurrency
                            : ""}
                        </Link>
                        <Link className="commonBtn">
                          {__("Available Balance:")} {userBalance.currency_code} {userBalance.wallets?.[0]?.balance_fiat}
                        </Link>
                      </div>
                    </div>
                  </>
                )}

                {activeWalletTab === "tip" && <BalanceTransfer {...props} />}

                {activeWalletTab === "vault" && <Vault {...props} />}
              </div>
            </div>
            <div className="clearfix"></div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default CashierModals;
